import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newsItems } from '@/mocks/newsData';

type NewsItem = typeof newsItems[0];

function NewsModal({ item, onClose, lang }: { item: NewsItem; onClose: () => void; lang: 'fr' | 'en' }) {
  const getTitle = (n: NewsItem) => (lang === 'en' ? n.titleEn : n.title);
  const getBody = (n: NewsItem) => (lang === 'en' ? n.bodyEn : n.body);
  const getCategory = (n: NewsItem) => (lang === 'en' ? n.categoryEn : n.category);
  const getDate = (n: NewsItem) => (lang === 'en' ? n.dateEn : n.date);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-foreground-950/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <article
        className="relative bg-background-50 rounded-2xl overflow-hidden max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image */}
        <div className="relative w-full h-56 md:h-72 flex-shrink-0 overflow-hidden">
          <img
            src={item.image}
            alt={getTitle(item)}
            className={`w-full h-full object-cover ${item.imageObjectPosition ?? 'object-center'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
            <span className="inline-block bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1 rounded-full mb-2">
              {getCategory(item)}
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 leading-tight">
              {getTitle(item)}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-calendar-line text-xs"></i>
            </div>
            <span className="text-sm text-foreground-500 font-medium">{getDate(item)}</span>
          </div>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
            {getBody(item)}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950/40 text-background-50 hover:bg-foreground-950/60 transition-all cursor-pointer backdrop-blur-sm"
          aria-label="Fermer"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </article>
    </div>
  );
}

export default function NewsSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'fr' | 'en';
  const [selectedItem, setSelectedItem] = useState<NewsItem | null>(null);

  const getDate = (item: NewsItem) => (lang === 'en' ? item.dateEn : item.date);
  const getTitle = (item: NewsItem) => (lang === 'en' ? item.titleEn : item.title);
  const getExcerpt = (item: NewsItem) => (lang === 'en' ? item.excerptEn : item.excerpt);
  const getCategory = (item: NewsItem) => (lang === 'en' ? item.categoryEn : item.category);
  const featured = newsItems.filter((n) => n.featured);

  return (
    <>
      <section id="news" className="py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-accent-600">{t('news.title')}</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mt-2">{t('news.subtitle')}</h2>
            </div>
          </div>

          {/* Featured */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {featured.slice(0, 2).map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className="group relative rounded-2xl overflow-hidden cursor-pointer block h-[320px] md:h-[380px] w-full text-left"
              >
                <img
                  src={item.image}
                  alt={getTitle(item)}
                  className={`w-full h-full object-cover ${item.imageObjectPosition ?? 'object-center'} transition-transform duration-700 group-hover:scale-105`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                  <span className="inline-block bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                    {getCategory(item)}
                  </span>
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-2">{getTitle(item)}</h3>
                  <p className="text-sm text-background-50/70 line-clamp-2">{getExcerpt(item)}</p>
                </div>
                {/* Read more hint */}
                <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newsItems.filter((n) => !n.featured).slice(0, 4).map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className="group bg-background-100 rounded-2xl overflow-hidden cursor-pointer block border border-background-200/70 transition-all duration-300 hover:border-accent-200/50 text-left w-full"
              >
                <div className="h-44 overflow-hidden relative">
                  <img
                    src={item.image}
                    alt={getTitle(item)}
                    className={`w-full h-full object-cover ${item.imageObjectPosition ?? 'object-center'} transition-transform duration-500 group-hover:scale-105`}
                  />
                  <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/10 transition-all duration-300"></div>
                </div>
                <div className="p-4">
                  <span className="text-xs text-accent-600 font-semibold">{getCategory(item)}</span>
                  <span className="text-xs text-foreground-400 ml-2">{getDate(item)}</span>
                  <h4 className="font-heading text-base font-semibold text-foreground-950 mt-1.5 mb-1.5 line-clamp-2">{getTitle(item)}</h4>
                  <p className="text-xs text-foreground-500 line-clamp-2">{getExcerpt(item)}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-accent-600 font-semibold">
                    <span>{lang === 'en' ? 'Read more' : 'Lire plus'}</span>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {selectedItem && (
        <NewsModal item={selectedItem} onClose={() => setSelectedItem(null)} lang={lang} />
      )}
    </>
  );
}